import { ExternalLink, Newspaper, RefreshCw } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { ApiError } from '@/core/api';
import { useFeedItems, useFeedSources, useRefreshFeedSource, type FeedKind } from '@/features/feeds';

interface FeedKindPageProps {
  kind: FeedKind;
  title: string;
  description: string;
}

/**
 * Shared layout for News-style sub-pages (News Feed, Market Trends).
 * Lists `/feeds/items/?kind=<kind>` with manual per-source refresh buttons.
 */
export function FeedKindPage({ kind, title, description }: FeedKindPageProps) {
  const items = useFeedItems({ kind });
  const sources = useFeedSources();
  const refresh = useRefreshFeedSource();

  const matchingSources = (sources.data?.results ?? []).filter((s) => s.kind === kind);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          <p className="text-muted-foreground text-sm">{description}</p>
        </div>
        {matchingSources.length ? (
          <div className="flex flex-wrap gap-2">
            {matchingSources.map((s) => (
              <Button
                key={s.id}
                size="sm"
                variant="outline"
                onClick={() => refresh.mutate(s.id)}
                disabled={refresh.isPending}
              >
                <RefreshCw className="size-4" /> {s.name}
              </Button>
            ))}
          </div>
        ) : null}
      </div>

      {items.isError ? (
        <p className="text-destructive text-sm">
          {(items.error as unknown as ApiError)?.message ?? 'Failed to load items.'}
        </p>
      ) : items.isPending ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : items.data && items.data.results.length ? (
        <div className="space-y-3">
          {items.data.results.map((it) => (
            <Card key={it.id}>
              <CardHeader>
                <CardTitle className="text-base">
                  {it.url ? (
                    <a
                      href={it.url}
                      target="_blank"
                      rel="noreferrer"
                      className="hover:text-primary flex items-start gap-2"
                    >
                      <span>{it.title}</span>
                      <ExternalLink className="mt-1 size-3.5 shrink-0" />
                    </a>
                  ) : (
                    it.title
                  )}
                </CardTitle>
                <CardDescription className="flex flex-wrap items-center gap-2 text-xs">
                  {it.published_at ? new Date(it.published_at).toLocaleString() : '—'}
                  {it.category ? (
                    <Badge variant="outline" className="text-[10px]">
                      {it.category}
                    </Badge>
                  ) : null}
                </CardDescription>
              </CardHeader>
              {it.summary ? (
                <CardContent>
                  <p className="text-muted-foreground line-clamp-3 text-sm">{it.summary}</p>
                </CardContent>
              ) : null}
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-muted-foreground flex flex-col items-center gap-2 rounded-md border border-dashed py-12 text-sm">
          <Newspaper className="size-6" />
          {matchingSources.length
            ? 'No items yet — try refreshing a source above.'
            : `No ${kind} sources configured. Add one in Settings › Integrations to start pulling.`}
        </div>
      )}
    </div>
  );
}
